import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, ReactNode, lazy } from "react";
import { AnimatePresence, motion } from "framer-motion";

import "./App.scss";

import Homepage from "./pages/Homepage";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { PageSuspense } from "./components/PageSuspense";
import BrancheSkeleton from "./components/skeletons/BrancheSkeleton";
import BranchDetailSkeleton from "./components/skeletons/BranchDetailSkeleton";
import GruppoSkeleton from "./components/skeletons/GruppoSkeleton";
import ContattiSkeleton from "./components/skeletons/ContattiSkeleton";
import NotFoundSkeleton from "./components/skeletons/NotFoundSkeleton";

const Branche = lazy(() => import("./pages/Branche"));
const LC = lazy(() => import("./pages/LC"));
const EG = lazy(() => import("./pages/EG"));
const RS = lazy(() => import("./pages/RS"));
const Gruppo = lazy(() => import("./pages/Gruppo"));
const Contatti = lazy(() => import("./pages/Contatti"));
const CoCa = lazy(() => import("./pages/CoCa"));
const NotFound = lazy(() => import("./pages/NotFound"));

interface PageLayoutProps {
  children: ReactNode;
}

const PageLayout = ({ children }: PageLayoutProps) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

const App = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  return (
    <>
      <div className="bg-tree" />
      <Header />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={<Homepage />}
          />
          <Route
            path="/branche"
            element={
              <PageLayout>
                <PageSuspense fallback={<BrancheSkeleton />}>
                  <Branche />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/branche/lc"
            element={
              <PageLayout>
                <PageSuspense fallback={<BranchDetailSkeleton withUniforme />}>
                  <LC />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/branche/eg"
            element={
              <PageLayout>
                <PageSuspense fallback={<BranchDetailSkeleton withUniforme />}>
                  <EG />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/branche/rs"
            element={
              <PageLayout>
                <PageSuspense fallback={<BranchDetailSkeleton />}>
                  <RS />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/branche/comunita-capi"
            element={
              <PageLayout>
                <PageSuspense fallback={<BranchDetailSkeleton withPdf />}>
                  <CoCa />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/gruppo"
            element={
              <PageLayout>
                <PageSuspense fallback={<GruppoSkeleton />}>
                  <Gruppo />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="/contatti"
            element={
              <PageLayout>
                <PageSuspense fallback={<ContattiSkeleton />}>
                  <Contatti />
                </PageSuspense>
              </PageLayout>
            }
          />
          <Route
            path="*"
            element={
              <PageLayout>
                <PageSuspense fallback={<NotFoundSkeleton />}>
                  <NotFound />
                </PageSuspense>
              </PageLayout>
            }
          />
        </Routes>
      </AnimatePresence>
      <Footer />
    </>
  );
};

export default App;
